import type { TFunction } from "i18next";

// Top-level error codes from cnut (backend/src/libs/exceptions/cnut-error-codes.ts).
// Subclass errors all collapse to one of these on the wire — the per-class detail
// lives in the `message` string (English) and is used as a fallback when no code
// is present or the code is unknown.
const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  "GENERIC.ARGUMENT_INVALID": "error.argumentInvalid",
  "GENERIC.ARGUMENT_OUT_OF_RANGE": "error.argumentOutOfRange",
  "GENERIC.ARGUMENT_NOT_PROVIDED": "error.argumentNotProvided",
  "DB_ERROR.BASE": "error.dbBase",
  "DB_ERROR.NOT_FOUND": "error.dbNotFound",
  "DB_ERROR.REQUEST_ERROR": "error.dbRequestError",
  "EXTERNAL_API_ERROR.BASE": "error.externalApiBase",
  "EXTERNAL_API_ERROR.NOT_FOUND": "error.externalApiNotFound",
  "EXTERNAL_API_ERROR.REQUEST_ERROR": "error.externalApiRequestError",
  "MODEL_ERROR.BASE": "error.modelBase",
  "MODEL_ERROR.EMPTY_ID": "error.modelEmptyId",
  "MODEL_ERROR.EMPTY_FIELD": "error.modelEmptyField",
  "MODEL_ERROR.INVALID_FIELD": "error.modelInvalidField",
  "SERVICE_ERROR.ALREADY_EXISTS": "error.serviceAlreadyExists",
  "SERVICE_ERROR.CLIENT": "error.serviceClient",
  "SERVICE_ERROR.SERVER": "error.serviceServer",
  "SERVICE_ERROR.VALIDATION_ERROR": "error.serviceValidation",
  "DATA_SOURCE_ERROR.BASE": "error.dataSourceBase",
  "SERVICE_ASYNC_JOB_ERROR.BASE": "error.asyncJobBase",
  "SERVICE_ASYNC_JOB_ERROR.IN_PROGRESS": "error.asyncJobInProgress",
  "SERVICE_ASYNC_JOB_ERROR.FAILED": "error.asyncJobFailed",
};

const extract = (error: unknown): { code?: string; message?: string } => {
  if (!error || typeof error !== "object") {
    return {};
  }
  const e = error as { code?: unknown; message?: unknown };
  return {
    code: typeof e.code === "string" ? e.code : undefined,
    message: typeof e.message === "string" ? e.message : undefined,
  };
};

// Translates a backend error to a localized message.
// - If the error carries a known top-level code, returns the translation for that code
// - Otherwise falls back to the raw `message` (typically the English detail from cnut)
// - If neither is available, returns the supplied fallback or a generic "unknown error" key.
export const translateError = (t: TFunction, error: unknown, fallback?: string): string => {
  const { code, message } = extract(error);
  if (code && ERROR_CODE_TRANSLATION_KEYS[code]) {
    return t(ERROR_CODE_TRANSLATION_KEYS[code]);
  }
  return message ?? fallback ?? t("error.unknown");
};
